import React, {
  ReactElement,
  useRef,
  useMemo,
  useEffect,
  useState,
} from "react";

import {
  StyledNavElement,
  StyledOList,
  StyledMenuList,
  StyledListWrapper,
  StyledBreadcrumbListItem,
} from "./Styles";
import { useForkRef, useResizeObserver } from "../../utils";
import { IconButton } from "../IconButton";
import { TreeDotsIcon } from "../Icons";
import { ListItem } from "../List";
import { Menu } from "../Menu";

export interface BreadcrumbsProps {
  /** Separator element */
  separator?: ReactElement | string;
}

export const Breadcrumbs = React.forwardRef<
  HTMLElement,
  BreadcrumbsProps &
    Omit<React.ComponentPropsWithoutRef<"nav">, keyof BreadcrumbsProps>
>(({ children, className, separator = "/", ...rest }, ref) => {
  const internalRef = useRef<HTMLElement | null>(null);
  const listWrapperRef = useRef<HTMLDivElement | null>(null);
  const olRef = useRef<HTMLOListElement | null>(null);
  const widthCollectionRef = useRef<number[]>([]);
  const handleRef = useForkRef(ref, internalRef);
  const [menuBreadcrumbs, setMenuBreadcrumbs] = useState(0);
  const dimensions = useResizeObserver(internalRef);
  const childDimensions = useResizeObserver(listWrapperRef);

  const parentWidth = dimensions?.width ?? 0;
  const childWidth = childDimensions?.width ?? 0;

  const childrenCollection = useMemo(
    () => React.Children.toArray(children),
    [children]
  );

  const [menuCollection, listCollection] = useMemo(() => {
    return [
      childrenCollection.slice(0, menuBreadcrumbs),
      childrenCollection.slice(menuBreadcrumbs, childrenCollection.length),
    ];
  }, [childrenCollection, menuBreadcrumbs]);

  useEffect(() => {
    widthCollectionRef.current = [];
    setMenuBreadcrumbs(0);
  }, [childrenCollection.length]);

  useEffect(() => {
    const diffWidth = childWidth - parentWidth;

    if (diffWidth > 0) {
      const separatorWidth =
        (
          olRef.current?.querySelectorAll(
            `[data-role="separator"]`
          )[0] as HTMLLIElement
        )?.offsetWidth ?? 0;
      const breadcrumbItems =
        olRef.current?.querySelectorAll(`[data-role="item"]`) ?? [];
      const childWidthCollection: number[] = [];

      const maxIndex = (breadcrumbItems?.length ?? 1) - 1;
      let currentIndex = 0;
      let childSumWidth = 0;

      while (diffWidth > childSumWidth && maxIndex >= currentIndex) {
        const childWidth =
          (breadcrumbItems[currentIndex] as HTMLLIElement)?.offsetWidth ?? 0;
        const result = separatorWidth + childWidth;
        childWidthCollection.push(result);
        childSumWidth += result;
        currentIndex += 1;
      }

      if (childWidthCollection.length === listCollection.length) {
        childWidthCollection.pop();
      }

      widthCollectionRef.current = [
        ...widthCollectionRef.current,
        ...childWidthCollection,
      ];

      setMenuBreadcrumbs((oldVal) => oldVal + childWidthCollection.length);
    }
  }, [listCollection.length, parentWidth, childWidth]);

  useEffect(() => {
    const diffWidth = parentWidth - childWidth;

    if (diffWidth > 0 && widthCollectionRef.current.length > 0) {
      const total = widthCollectionRef.current.length;
      const collection = [...widthCollectionRef.current];

      let sumWidth = 0;

      while (diffWidth > sumWidth && collection.length > 0) {
        const lastWidth = collection[collection.length - 1] ?? 0;
        if (diffWidth < lastWidth + sumWidth) {
          sumWidth = diffWidth;
        } else {
          sumWidth += collection.pop() ?? 0;
        }
      }

      const totalToDecrease = total - collection.length;

      setMenuBreadcrumbs((oldVal) => {
        const newValue = oldVal - totalToDecrease;
        return newValue > 0 ? newValue : 0;
      });

      const end = total - totalToDecrease;

      widthCollectionRef.current =
        end > 0 ? widthCollectionRef.current.slice(0, end) : [];
    }
  }, [parentWidth, childWidth]);

  return (
    <StyledNavElement className={className} ref={handleRef} {...rest}>
      <StyledListWrapper ref={listWrapperRef}>
        <StyledOList ref={olRef}>
          {menuCollection.length > 0 ? (
            <StyledBreadcrumbListItem data-role="menu">
              <Menu
                target={
                  <IconButton variant="text gray">
                    <TreeDotsIcon />
                  </IconButton>
                }
              >
                <StyledMenuList>
                  {menuCollection.map((child, index) => (
                    <ListItem key={index}>{child}</ListItem>
                  ))}
                </StyledMenuList>
              </Menu>
            </StyledBreadcrumbListItem>
          ) : null}
          {listCollection.map((child, index) => {
            return (
              <React.Fragment key={index}>
                {index > 0 || menuCollection.length > 0 ? (
                  <StyledBreadcrumbListItem data-role="separator">
                    {separator}
                  </StyledBreadcrumbListItem>
                ) : null}
                <StyledBreadcrumbListItem data-role="item">
                  {child}
                </StyledBreadcrumbListItem>
              </React.Fragment>
            );
          })}
        </StyledOList>
      </StyledListWrapper>
    </StyledNavElement>
  );
});

Breadcrumbs.displayName = "Breadcrumbs";
